import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";
import { BRAND } from "@/content/brand";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

/**
 * GuidePage: the layout for every learn / buyer guide page.
 *
 * Written for people first and answer engines second:
 *  - a one paragraph direct answer ("tldr") right under the H1, which is
 *    what Google AI Overviews, ChatGPT and Perplexity tend to quote
 *  - question shaped H2s, short paragraphs, tables where they help
 *  - visible sources, a reviewed date, and Article + FAQ + Breadcrumb JSON-LD
 *
 * Props:
 *  path, seoTitle, seoDescription, eyebrow, title, tldr
 *  updated: "2026-09-24" (ISO date, shown and used in JSON-LD)
 *  sections: [{ id, heading, body: JSX }]
 *  faqs: [{ q, a }]  (plain text answers)
 *  sources: [{ label, url }]
 *  related: [{ label, to }]
 *  testid
 */
export default function GuidePage({
  path,
  seoTitle,
  seoDescription,
  eyebrow = "Learn",
  title,
  tldr,
  updated,
  sections = [],
  faqs = [],
  sources = [],
  related = [],
  testid,
}) {
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: seoDescription,
    datePublished: updated,
    dateModified: updated,
    mainEntityOfPage: `${SITE_URL}${path}`,
    author: { "@type": "Organization", name: BRAND.name, url: SITE_URL },
    publisher: { "@type": "Organization", name: BRAND.company, brand: BRAND.name, url: SITE_URL },
    citation: sources.map((s) => s.url),
  };
  const jsonLd = [
    articleLd,
    breadcrumbLd([{ name: "Home", path: "/" }, { name: title, path }]),
    ...(faqs.length ? [faqLd(faqs.map((f) => ({ question: f.q, answer: f.a })))] : []),
  ];

  return (
    <>
      <Seo title={seoTitle || title} description={seoDescription} type="article" jsonLd={jsonLd} url={`${SITE_URL}${path}`} />

      <article data-testid={testid} className="bg-brand-parchment">
        <header className="max-w-3xl mx-auto px-6 pt-16 sm:pt-24">
          <nav aria-label="Breadcrumb" className="font-body text-[13px] text-ink-muted">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span>{eyebrow}</span>
          </nav>
          <h1 className="mt-4 font-display text-[clamp(2.4rem,6vw,4.25rem)] leading-[1.02] tracking-display text-brand-ink">{title}</h1>
          {tldr && (
            <div className="mt-8 rounded-3xl bg-brand-parchment-soft p-7" data-testid="guide-tldr">
              <p className="font-body text-[13px] font-semibold text-brand-gold">The short answer</p>
              <p className="mt-2 font-body text-[18px] sm:text-[19px] leading-relaxed text-brand-ink">{tldr}</p>
            </div>
          )}
          {updated && (
            <p className="mt-6 font-body text-[13px] text-ink-muted">
              By the Karijeeva team at {BRAND.company}. Last reviewed{" "}
              <time dateTime={updated}>{new Date(updated).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</time>.
            </p>
          )}
        </header>

        {sections.length > 2 && (
          <nav aria-label="On this page" className="max-w-3xl mx-auto px-6 mt-10">
            <p className="font-body text-[13px] font-semibold text-brand-ink">On this page</p>
            <ol className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 font-body text-[15px]">
              {sections.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="text-brand-gold hover:underline">{s.heading}</a></li>
              ))}
            </ol>
          </nav>
        )}

        <div className="max-w-3xl mx-auto px-6 pb-8 guide-prose">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-20">
              <h2>{s.heading}</h2>
              {s.body}
            </section>
          ))}
        </div>

        <aside className="max-w-3xl mx-auto px-6 my-12">
          <div className="rounded-3xl bg-brand-obsidian text-brand-bone p-8 sm:p-10">
            <p className="font-display text-white text-[28px] leading-tight tracking-heading">Cold pressed coconut oil from our family farms.</p>
            <p className="mt-3 font-body text-brand-bone/75 leading-relaxed">
              One ingredient. Grown in Tiptur and on the Karnataka coast, pressed in small batches, every batch tested at an independent food testing lab. FSSAI licence {BRAND.fssai}.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              <Link to="/products" className="rounded-control bg-brand-gold hover:bg-brand-gold-deep px-6 py-3 font-body text-[15px] text-white">Shop Karijeeva</Link>
              <Link to="/lab-reports" className="inline-flex items-center gap-1 py-3 font-body text-[15px] text-white hover:underline">See the lab report <ChevronRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </aside>

        {faqs.length > 0 && (
          <section className="max-w-3xl mx-auto px-6 pb-8" aria-labelledby="guide-faqs">
            <h2 id="guide-faqs" className="font-display text-[32px] tracking-heading text-brand-ink">Questions people ask</h2>
            <div className="mt-6 divide-y divide-black/10 border-y border-black/10">
              {faqs.map((f) => (
                <div key={f.q} className="py-5">
                  <h3 className="font-display text-[19px] text-brand-ink">{f.q}</h3>
                  <p className="mt-2 font-body text-[16px] leading-relaxed text-ink-muted">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {sources.length > 0 && (
          <section className="max-w-3xl mx-auto px-6 pb-10" aria-labelledby="guide-sources">
            <h2 id="guide-sources" className="font-display text-[20px] text-brand-ink">Sources</h2>
            <ol className="mt-3 list-decimal pl-5 space-y-1 font-body text-[14px] text-ink-muted">
              {sources.map((s) => (
                <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline break-words">{s.label}</a></li>
              ))}
            </ol>
          </section>
        )}

        {related.length > 0 && (
          <nav className="max-w-3xl mx-auto px-6 pb-24" aria-label="Related guides">
            <p className="font-body text-[13px] font-semibold text-brand-ink">Keep reading</p>
            <ul className="mt-3 flex flex-wrap gap-3">
              {related.map((r) => (
                <li key={r.to}>
                  <Link to={r.to} className="inline-flex rounded-control bg-brand-parchment-soft px-4 py-2 font-body text-[14px] text-brand-ink hover:bg-black/5">{r.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </article>
    </>
  );
}
